import { businessStore } from 'entities/index';
import { WorkingHoursPicker } from 'features/index';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, DefaultButton, FormInput } from 'shared/ui';
import styles from './point-settings.module.scss';
import { BusinessApi } from 'shared/api';
import { AppLinks } from 'app/router/consts/app-links.enum';
import { useNavigate } from 'react-router-dom';
import { pointSettingsStore } from '../model/point-settings.store';

export const PointSettings: React.FC = observer(() => {
  const pointId = businessStore.pointId;
  const businessId = businessStore.businessId;
  const navigate = useNavigate();
  const [alertMessage, setAlertMessage] = useState<string | null>(null);

  useEffect(() => {
    const data = businessStore.pointHeaderData;
    if (data && data.id === pointId) {
      pointSettingsStore.initFromPointData(data);
    }
    return () => {
      if (businessStore.pointId !== pointId) {
        pointSettingsStore.reset();
      }
    };
  }, [businessStore.pointHeaderData, pointId]);

  const handleCloseAlert = useCallback(() => setAlertMessage(null), []);

  const handleSubmit = async () => {
    try {
      await pointSettingsStore.save();
      setAlertMessage('Изменения успешно сохранены');
    } catch (error) {
      // validationError is already set in the store
    }
  };

  async function handleDelete() {
    if (businessId && pointId) {
      try {
        await BusinessApi.deletePoint({ pointId });
        setAlertMessage('Точка успешно удалена');
        setTimeout(() => {
          const path = AppLinks.BusinessLink.replace(':id', businessId);
          navigate(path);
        }, 1500);
      } catch (error) {
        setAlertMessage(
          error instanceof Error ? error.message : 'Ошибка при удалении точки'
        );
      }
    }
  }

  return (
    <div className={styles.point_settings}>
      <div className={styles.point_settings__content}>
        <div className={styles.content__inputs}>
          <div className={styles.inputs_block}>
            <div className={styles.inputs__input}>
              <FormInput
                inputValue={pointSettingsStore.name}
                inputTitle="Название точки"
                inputPlaceholder="Название точки"
                onChange={(value) => {
                  pointSettingsStore.setField('name', value);
                }}
              />
            </div>
            <div className={styles.inputs__input}>
              <FormInput
                inputValue={pointSettingsStore.description}
                inputTitle="Описание"
                inputPlaceholder="Введите описание"
                onChange={(value) => {
                  pointSettingsStore.setField('description', value);
                }}
              />
            </div>
            <div className={styles.inputs__input}>
              <FormInput
                inputValue={pointSettingsStore.address}
                inputTitle="Адрес точки"
                inputPlaceholder="Адрес точки"
                onChange={(value) => {
                  pointSettingsStore.setField('address', value);
                }}
              />
            </div>
            <div className={styles.inputs__input}>
              <FormInput
                inputValue={pointSettingsStore.contactPhone}
                isPhone={true}
                inputTitle="Номер телефона для связи"
                onChange={(value) => {
                  pointSettingsStore.setField(
                    'contactPhone',
                    value.match(/\d+/g)?.join('') ?? ''
                  );
                }}
              />
            </div>
            <div className={styles.inputs__input}>
              <FormInput
                inputType="site"
                inputValue={pointSettingsStore.siteUrl}
                inputTitle="Ссылка на сайт"
                inputPlaceholder="Введите адрес сайта"
                onChange={(value) => {
                  pointSettingsStore.setField('siteUrl', value);
                }}
              />
            </div>
          </div>
          <div className={styles.inputs_block}>
            <div className={styles.inputs__input}>
              <WorkingHoursPicker
                inputTitle="График работы"
                onChangeTimeTableHandler={(value) => {
                  pointSettingsStore.setWorkingHours(
                    value === null ? 'employee' : 'fixed',
                    value
                  );
                }}
                inputTimeTable={
                  pointSettingsStore.isEmployeeMode
                    ? null
                    : pointSettingsStore.localWeekWorkTime
                }
                showEmployeeOption={true}
                isWorkTimeByEmployee={pointSettingsStore.isEmployeeMode}
                onSwitchChange={(active) =>
                  pointSettingsStore.setSwitchActive(active)
                }
              />
            </div>
          </div>
        </div>
        <div className={styles.buttons__error}>
          {pointSettingsStore.validationError}
        </div>
        <div className={styles.content__buttons}>
          <div className={styles.buttons_button} onClick={handleDelete}>
            <DefaultButton buttonText="Удалить точку" color="red" />
          </div>
          <div className={styles.buttons_button} onClick={handleSubmit}>
            <DefaultButton
              buttonText="Сохранить изменения"
              color="white"
              className={styles.button_save}
            />
          </div>
        </div>
      </div>
      {alertMessage && (
        <Alert
          type="success"
          message={alertMessage}
          onClose={handleCloseAlert}
        />
      )}
    </div>
  );
});
